// ─────────────────────────────────────────────────────────────
// AWS Cognito authentication helpers
//
// Wraps amazon-cognito-identity-js so the rest of the app deals
// with plain promises instead of the SDK's callback style.
// ─────────────────────────────────────────────────────────────
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

// Config — overridable at runtime via window._env_ (see public/env-config.js)
// or at build time via REACT_APP_* env vars, falling back to the pool that
// was provisioned for this project.
const cfg = (key, fallback) =>
  (window._env_ && window._env_[key]) || process.env[key] || fallback;

export const USER_POOL_ID = cfg('REACT_APP_USER_POOL_ID', 'eu-west-2_ZGX3PZ5NL');
export const CLIENT_ID    = cfg('REACT_APP_USER_POOL_CLIENT_ID', 'geu6i242ssqie6jpfios7h0uv');
export const REGION       = cfg('REACT_APP_AWS_REGION', 'eu-west-2');

export const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId:   CLIENT_ID,
});

// ── Internal state ────────────────────────────────────────────
// Holds the CognitoUser created during a NEW_PASSWORD_REQUIRED
// challenge so completeNewPassword() can finish the same flow.
let pendingUser = null;
let pendingUserAttributes = null;

// ─────────────────────────────────────────────────────────────
// signIn — username/email + password (SRP)
//
// Resolves with { status: 'SUCCESS', session } on a normal login,
// or { status: 'NEW_PASSWORD_REQUIRED', userAttributes } when the
// account was created by an admin and must set its own password.
// ─────────────────────────────────────────────────────────────
export function signIn(username, password) {
  const user = new CognitoUser({ Username: username, Pool: userPool });
  const details = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: (session) => {
        pendingUser = null;
        pendingUserAttributes = null;
        resolve({ status: 'SUCCESS', session });
      },
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // These two attributes are read-only and must not be re-submitted.
        delete userAttributes.email_verified;
        delete userAttributes.email;
        pendingUser = user;
        pendingUserAttributes = userAttributes;
        resolve({ status: 'NEW_PASSWORD_REQUIRED', userAttributes });
      },
    });
  });
}

// ─────────────────────────────────────────────────────────────
// completeNewPassword — finishes the NEW_PASSWORD_REQUIRED flow
// ─────────────────────────────────────────────────────────────
export function completeNewPassword(newPassword) {
  return new Promise((resolve, reject) => {
    if (!pendingUser) {
      reject(new Error('No password-change challenge in progress'));
      return;
    }
    pendingUser.completeNewPasswordChallenge(newPassword, pendingUserAttributes || {}, {
      onSuccess: (session) => {
        pendingUser = null;
        pendingUserAttributes = null;
        resolve({ status: 'SUCCESS', session });
      },
      onFailure: (err) => reject(err),
    });
  });
}

// ─────────────────────────────────────────────────────────────
// signUp — self-service registration.
//
// Creates a new account in the user pool. Cognito then emails a
// confirmation code that must be passed to confirmSignUp() before
// the account can sign in.
//
// Requires self-registration to be enabled on the user pool
// (Cognito → User pool → Sign-up experience → Self-service sign-up).
//
// Resolves with { userConfirmed, codeDelivery } where userConfirmed
// is false when an email confirmation step is still required.
// ─────────────────────────────────────────────────────────────
export function signUp(email, password) {
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: email }),
  ];

  return new Promise((resolve, reject) => {
    userPool.signUp(email, password, attributes, null, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve({
        username:      result.user.getUsername(),
        userConfirmed: result.userConfirmed,
        codeDelivery:  result.codeDeliveryDetails,
      });
    });
  });
}

// ─────────────────────────────────────────────────────────────
// confirmSignUp — verifies the emailed confirmation code, which
// activates the account so it can sign in.
// ─────────────────────────────────────────────────────────────
export function confirmSignUp(email, code) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.confirmRegistration(code, true, (err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });
}

// ─────────────────────────────────────────────────────────────
// resendConfirmationCode — re-sends the sign-up confirmation email.
// ─────────────────────────────────────────────────────────────
export function resendConfirmationCode(email) {
  const user = new CognitoUser({ Username: email, Pool: userPool });
  return new Promise((resolve, reject) => {
    user.resendConfirmationCode((err, result) => {
      if (err) reject(err);
      else resolve(result);
    });
  });
}

// ─────────────────────────────────────────────────────────────
// getSession — returns the current (auto-refreshed) Cognito session
// or null if there is no signed-in user / the session is invalid.
// ─────────────────────────────────────────────────────────────
export function getSession() {
  return new Promise((resolve) => {
    const user = userPool.getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }
    user.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
}

// Convenience: the signed-in user's email/username, or null.
export async function getCurrentUserInfo() {
  const session = await getSession();
  if (!session) return null;
  const payload = session.getIdToken().decodePayload();
  return {
    email:    payload.email || payload['cognito:username'] || payload.sub,
    username: payload['cognito:username'] || payload.sub,
    sub:      payload.sub,
  };
}

// Returns a valid ID token string, or null.
export async function getIdToken() {
  const session = await getSession();
  return session ? session.getIdToken().getJwtToken() : null;
}

// ─────────────────────────────────────────────────────────────
// signOut — clears the local Cognito session
// ─────────────────────────────────────────────────────────────
export function signOut() {
  const user = userPool.getCurrentUser();
  if (user) user.signOut();
}

// ─────────────────────────────────────────────────────────────
// authFetch — fetch() wrapper that attaches the Cognito ID token
// as a Bearer token. A 401 means the token was rejected; callers
// can treat that as "session expired".
// ─────────────────────────────────────────────────────────────
export async function authFetch(url, options = {}) {
  const token = await getIdToken();
  const headers = { ...(options.headers || {}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}
